import { z } from 'zod';
import { EmailSchema, EpochMsSchema, SlugSchema, UlidSchema } from './common';

export const DomainSchema = z
  .string()
  .min(3)
  .max(253)
  .regex(/^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.(?!-)[A-Za-z0-9-]{1,63}(?<!-))+$/, 'Expected a domain name');

export const DomainVerifyStartSchema = z.object({ domain: DomainSchema });
export const DomainVerifyStartResultSchema = z.object({
  domain: DomainSchema,
  txtRecordName: z.string(),
  txtRecordValue: z.string(),
  instructions: z.string(),
});
export const DomainVerifyCheckResultSchema = z.object({
  domain: DomainSchema,
  verified: z.boolean(),
  verifiedAt: EpochMsSchema.nullable(),
  reason: z.string().nullable(),
});

export const WorkEmailStartSchema = z.object({ orgId: UlidSchema, email: EmailSchema });
export const WorkEmailConfirmSchema = z.object({ orgId: UlidSchema, code: z.string().min(4).max(16) });
export const WorkEmailStartResultSchema = z.object({ status: z.literal('code_sent') });
export const WorkEmailConfirmResultSchema = z.object({
  verified: z.literal(true),
  email: EmailSchema,
  orgId: UlidSchema,
});

export const VERIFIED_BADGE_KINDS = ['work_email', 'domain'] as const;
export const VerifiedBadgeKindSchema = z.enum(VERIFIED_BADGE_KINDS);
/**
 * Public-safe badge shape. PRIVACY: never carries the verified work email — only the domain
 * portion, plus which org (id/name) it was verified against and when. The raw email is returned
 * only to its owner, from POST /verification/work-email/confirm and the account data export.
 */
export const VerifiedBadgeSchema = z.object({
  kind: VerifiedBadgeKindSchema,
  orgId: UlidSchema.nullable(),
  orgName: z.string().nullable(),
  domain: DomainSchema,
  verifiedAt: EpochMsSchema,
});
export type VerifiedBadge = z.infer<typeof VerifiedBadgeSchema>;

export const VerificationOrgIdParamSchema = z.object({ id: UlidSchema });
export const OrgSlugPublicParamsSchema = z.object({ slug: SlugSchema });
export const ProfileHandlePublicParamsSchema = z.object({ handle: z.string().min(1).max(60) });

export const OrgPublicExtrasSchema = z.object({
  orgId: UlidSchema,
  slug: SlugSchema,
  verifiedBadges: z.array(VerifiedBadgeSchema),
  followerCount: z.number().int().nonnegative(),
  employees: z.array(
    z.object({
      userId: UlidSchema,
      handle: z.string().nullable(),
      displayName: z.string(),
      verifiedBadges: z.array(VerifiedBadgeSchema),
    }),
  ),
});

export const ProfilePublicExtrasSchema = z.object({
  userId: UlidSchema,
  handle: z.string(),
  verifiedBadges: z.array(VerifiedBadgeSchema),
});

/** A23: opt-in for org members to appear in the org's public employee list. */
export const EmployeeVisibilityInputSchema = z.object({ show: z.boolean() });
export const EmployeeVisibilityResultSchema = z.object({ orgId: UlidSchema, show: z.boolean() });
